'use client'

import { AlertTriangle, Scissors, Clock } from 'lucide-react'
import { DateTime } from 'luxon'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

import { OverlapDetails } from '../../utils/priorityCalculator'

interface OverlapConfirmationModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	overlapDetails: OverlapDetails | null
	onConfirmSplit: () => void
	onCancel: () => void
}

function formatTime(date: Date): string {
	return DateTime.fromJSDate(date).toLocaleString(DateTime.TIME_SIMPLE)
}

export const OverlapConfirmationModal = ({
	open,
	onOpenChange,
	overlapDetails,
	onConfirmSplit,
	onCancel,
}: OverlapConfirmationModalProps) => {
	const { t } = useTranslation()
	if (!overlapDetails) return null

	const {
		existingEvent,
		overlapStart,
		overlapEnd,
		existingStart,
		existingEnd,
		newStart,
		newEnd,
		scenario,
	} = overlapDetails

	// Calculate what will happen after the split
	const willCreateRemainder = scenario === 'new_inside_existing'
	const willDeleteExisting = scenario === 'new_covers_existing'

	const getScenarioDescription = (
		scenarioType: OverlapDetails['scenario']
	): string => {
		switch (scenarioType) {
			case 'new_inside_existing':
				return t('entities.schedule.overlap.scenario_new_inside')
			case 'new_overlaps_end':
				return t('entities.schedule.overlap.scenario_overlaps_end')
			case 'new_overlaps_start':
				return t('entities.schedule.overlap.scenario_overlaps_start')
			case 'new_covers_existing':
				return t('entities.schedule.overlap.scenario_covers_existing')
			default:
				return t('entities.schedule.overlap.generic_overlap')
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-4">
					<div className="flex items-center gap-3">
						<div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
							<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
						</div>
						<div>
							<DialogTitle className="text-lg">
								{t('entities.schedule.overlap.timeslot_overlap_detected')}
							</DialogTitle>
							<DialogDescription className="text-sm mt-1">
								{getScenarioDescription(scenario)}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-5 space-y-4">
					{/* Existing Slot Info */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t('entities.schedule.overlap.existing_slot')}
						</h4>
						<div className="p-3 rounded-lg border bg-muted/30">
							<div className="flex items-center gap-2">
								<div
									className="w-3 h-3 rounded-full"
									style={{
										backgroundColor:
											existingEvent.backgroundColor || existingEvent.color,
									}}
								/>
								<span className="font-medium text-sm">
									{existingEvent.title}
								</span>
							</div>
							<div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
								<Clock className="w-3 h-3" />
								<span>
									{formatTime(existingStart)} - {formatTime(existingEnd)}
								</span>
							</div>
						</div>
					</div>

					{/* Visual Timeline */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t('entities.schedule.overlap.after_split')}
						</h4>
						<div className="relative h-24 bg-muted/30 rounded-lg border overflow-hidden">
							{/* Timeline background grid */}
							<div className="absolute inset-0 flex">
								{[...Array(8)].map((_, i) => (
									<div
										key={i}
										className="flex-1 border-r border-muted last:border-r-0"
									/>
								))}
							</div>

							{/* Time labels */}
							<div className="absolute top-1 left-0 right-0 flex justify-between px-2 text-[10px] text-muted-foreground">
								<span>
									{formatTime(
										new Date(
											Math.min(existingStart.getTime(), newStart.getTime())
										)
									)}
								</span>
								<span>
									{formatTime(
										new Date(Math.max(existingEnd.getTime(), newEnd.getTime()))
									)}
								</span>
							</div>

							{/* Slot visualization */}
							<div className="absolute bottom-2 left-2 right-2 h-14 flex flex-col gap-1">
								{/* Existing slot (modified) */}
								{!willDeleteExisting && (
									<div className="relative h-6 flex gap-1">
										{/* Before overlap part - shown for new_inside_existing and new_overlaps_end */}
										{scenario !== 'new_overlaps_start' && (
											<div
												className="h-full rounded flex items-center justify-center text-[10px] text-white font-medium"
												style={{
													backgroundColor:
														existingEvent.backgroundColor ||
														existingEvent.color,
													flex: `${overlapStart.getTime() - existingStart.getTime()} 0 0`,
												}}
											>
												<span className="truncate px-1">
													{t('entities.schedule.overlap.existing_kept')}
												</span>
											</div>
										)}
										{/* After overlap part (remainder) */}
										{willCreateRemainder && (
											<div
												className="h-full rounded flex items-center justify-center text-[10px] text-white font-medium"
												style={{
													backgroundColor:
														existingEvent.backgroundColor ||
														existingEvent.color,
													opacity: 0.7,
													flex: `${existingEnd.getTime() - overlapEnd.getTime()} 0 0`,
												}}
											>
												<span className="truncate px-1">
													{t('entities.schedule.overlap.remainder')}
												</span>
											</div>
										)}
										{/* Modified existing slot for overlaps_start */}
										{scenario === 'new_overlaps_start' && (
											<div
												className="h-full rounded flex items-center justify-center text-[10px] text-white font-medium ml-auto"
												style={{
													backgroundColor:
														existingEvent.backgroundColor ||
														existingEvent.color,
													flex: `${existingEnd.getTime() - overlapEnd.getTime()} 0 0`,
												}}
											>
												<span className="truncate px-1">
													{t('entities.schedule.overlap.existing_kept')}
												</span>
											</div>
										)}
									</div>
								)}

								{/* New slot */}
								<div className="relative h-6">
									<div
										className="absolute h-full rounded flex items-center justify-center text-[10px] font-medium border-2 border-dashed border-primary bg-primary/20 text-primary"
										style={{
											left: `${((newStart.getTime() - Math.min(existingStart.getTime(), newStart.getTime())) / (Math.max(existingEnd.getTime(), newEnd.getTime()) - Math.min(existingStart.getTime(), newStart.getTime()))) * 100}%`,
											width: `${((newEnd.getTime() - newStart.getTime()) / (Math.max(existingEnd.getTime(), newEnd.getTime()) - Math.min(existingStart.getTime(), newStart.getTime()))) * 100}%`,
										}}
									>
										<span className="truncate px-1">
											{t('entities.schedule.overlap.new_slot')}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* What will happen */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t('entities.schedule.overlap.what_will_happen')}
						</h4>
						<ul className="space-y-1.5 text-sm">
							{scenario === 'new_covers_existing' ? (
								<li className="flex items-start gap-2">
									<span className="text-red-500 mt-0.5">•</span>
									<span>
										{t('entities.schedule.overlap.will_be_deleted', {
											title: existingEvent.title,
										})}
									</span>
								</li>
							) : scenario === 'new_overlaps_start' ? (
								<li className="flex items-start gap-2">
									<span className="text-amber-500 mt-0.5">•</span>
									<span>
										{t('entities.schedule.overlap.will_start_at', {
											title: existingEvent.title,
											time: formatTime(overlapEnd),
										})}
									</span>
								</li>
							) : (
								<li className="flex items-start gap-2">
									<span className="text-amber-500 mt-0.5">•</span>
									<span>
										{t('entities.schedule.overlap.will_end_at', {
											title: existingEvent.title,
											time: formatTime(overlapStart),
										})}
									</span>
								</li>
							)}
							{willCreateRemainder && (
								<li className="flex items-start gap-2">
									<span className="text-blue-500 mt-0.5">•</span>
									<span>
										{t('entities.schedule.overlap.new_slot_created', {
											start: formatTime(overlapEnd),
											end: formatTime(existingEnd),
										})}
									</span>
								</li>
							)}
							<li className="flex items-start gap-2">
								<span className="text-green-500 mt-0.5">•</span>
								<span>
									{t('entities.schedule.overlap.your_slot_created', {
										start: formatTime(newStart),
										end: formatTime(newEnd),
									})}
								</span>
							</li>
						</ul>
					</div>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4 gap-2 sm:gap-0">
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						className="h-9 rounded-full px-6"
					>
						{t('actions.cancel')}
					</Button>
					<Button
						type="button"
						onClick={onConfirmSplit}
						className="h-9 rounded-full px-6 gap-2"
					>
						<Scissors className="w-4 h-4" />
						{t('entities.schedule.overlap.split_and_continue')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
